import {
  type DeletePaymentMethodResponseDto,
  type PaymentMethodListResponseDto,
  PaymentMethodsApi,
  type SetDefaultPaymentMethodResponseDto,
  type SetupCheckoutResponseDto,
} from '@saaga/billingv2-sdk'
import { useCallback } from 'react'

import { useBillingConfig } from './use-billing-config.hook'

export interface UsePaymentApiReturn {
  getPaymentMethodsClient: () => Promise<PaymentMethodsApi>
  setupPaymentMethodCheckout: (returnUrl: string) => Promise<SetupCheckoutResponseDto>
  getPaymentMethods: () => Promise<PaymentMethodListResponseDto>
  setDefaultPaymentMethod: (paymentMethodId: string) => Promise<SetDefaultPaymentMethodResponseDto>
  deletePaymentMethod: (paymentMethodId: string) => Promise<DeletePaymentMethodResponseDto>
}

export const usePaymentApi = (): UsePaymentApiReturn => {
  const { createConfig } = useBillingConfig()

  const getPaymentMethodsClient = useCallback(async () => {
    const config = await createConfig()
    return new PaymentMethodsApi(config)
  }, [createConfig])

  const setupPaymentMethodCheckout = useCallback(
    async (returnUrl: string): Promise<SetupCheckoutResponseDto> => {
      const client = await getPaymentMethodsClient()
      return client.paymentMethodsControllerSetupPaymentMethodCheckout({
        setupCheckoutRequestDto: { returnUrl },
      })
    },
    [getPaymentMethodsClient]
  )

  const getPaymentMethods = useCallback(async (): Promise<PaymentMethodListResponseDto> => {
    const client = await getPaymentMethodsClient()
    return client.paymentMethodsControllerGetPaymentMethods()
  }, [getPaymentMethodsClient])

  const setDefaultPaymentMethod = useCallback(
    async (paymentMethodId: string): Promise<SetDefaultPaymentMethodResponseDto> => {
      const client = await getPaymentMethodsClient()
      return client.paymentMethodsControllerSetDefaultPaymentMethod({ paymentMethodId })
    },
    [getPaymentMethodsClient]
  )

  const deletePaymentMethod = useCallback(
    async (paymentMethodId: string): Promise<DeletePaymentMethodResponseDto> => {
      const client = await getPaymentMethodsClient()
      return client.paymentMethodsControllerDeletePaymentMethod({ paymentMethodId })
    },
    [getPaymentMethodsClient]
  )

  return {
    getPaymentMethodsClient,
    setupPaymentMethodCheckout,
    getPaymentMethods,
    setDefaultPaymentMethod,
    deletePaymentMethod,
  }
}
